import { CircularProgress, Collapse, Grid, Typography } from '@mui/material';
import { keyframes, styled } from '@mui/material/styles';
import { COMPONENT_ICON_ALT } from '../../../../../../../utils/CanvasConstants';
import { hexToPastel } from '../../../../../../../utils/HexToPastelUtil';
import useZoomCollapse from '../../../../../../../hooks/useZoomCollapse';
import AiInstructionInfoText from './AiInstructionInfoText';
import AILoadingAnimation from '../../../../../../common/AILoadingAnimation';
import React from 'react';
import * as PropTypes from 'prop-types';

const DB_TYPE = 'UI.dbIcon';

// Add shimmer animation for loading states
const shimmer = keyframes`
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
`;

// Add typing animation for waiting state
const typingAnimation = keyframes`
  0% { opacity: 0.3; transform: scale(0.8); }
  50% { opacity: 1; transform: scale(1.2); }
  100% { opacity: 0.3; transform: scale(0.8); }
`;

// Add this function to filter out the prop
const shouldForwardProp = prop =>
  prop !== 'hasaidraftbanner' && prop !== 'animationType' && prop !== 'baseColor' && prop !== 'hasloop';

const NodeBodyContainer = styled(Grid, { shouldForwardProp })(
  ({ theme, baseColor, type, animationType, hasaidraftbanner: hasAIDraftBanner, hasloop: hasLoop }) => ({
    border: hasAIDraftBanner
      ? `3px solid #944BA8`
      : `1px solid ${type === DB_TYPE ? theme.palette.text.primary : baseColor}`,
    overflow: 'hidden',
    position: 'relative', // Added for absolute positioning of animation
    // Add shimmer effect for loading states
    ...(animationType && {
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background:
          'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.4) 50%, transparent 100%)',
        animation: `${shimmer} 1.5s infinite`,
        zIndex: 1,
        pointerEvents: 'none',
      },
    }),
  })
);

// Animation container in corner
const AnimationContainer = styled('div')({
  position: 'absolute',
  top: '5px',
  right: '5px',
  zIndex: 10,
  background: 'none',
});

// Typing animation for waiting state
const TypingDots = styled('div')(({ color }) => ({
  fontWeight: 'bold',
  color: color || '#5d6bc4', // Default to blue/purple for waiting
  fontSize: '16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '3px',
}));

const Dot = styled('span')(({ delay }) => ({
  width: '6px',
  height: '6px',
  backgroundColor: 'currentColor',
  borderRadius: '50%',
  display: 'inline-block',
  animation: `${typingAnimation} 1.5s infinite`,
  animationDelay: delay,
}));

const headerShouldForwardPropFull = prop =>
  prop !== 'stateColor' && prop !== 'baseColor' && prop !== 'collapsed';

const HeaderContainer = styled(Grid, { shouldForwardProp: headerShouldForwardPropFull })(
  ({ theme, baseColor, stateColor, collapsed }) => ({
    background: stateColor
      ? hexToPastel(stateColor, theme.palette.mode)
      : hexToPastel(baseColor, theme.palette.mode),
    padding: collapsed ? '4px 0px 12px 0px' : '0px 0px 8px 0px',
    '& > .MuiGrid-item': {
      padding: collapsed ? '0px 14px 0px 14px' : '0px 8px 0px 8px',
    },
    transition: 'padding 0.2s ease',
  })
);

const HeaderTitleGrid = styled(Grid)({
  paddingTop: '8px !important',
});

const ComponentLabelText = styled(Typography)({
  fontFamily: 'Open Sans !important',
  fontSize: '12px',
  fontWeight: '400 !important',
  lineHeight: '16px',
  textAlign: 'left',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const ComponentDescriptionContainer = styled(Grid)(({ theme }) => ({
  background: theme.palette.background.paper,
  '& > .MuiGrid-item': {
    padding: '8px 8px 8px 8px',
  },
}));

const ComponentDescriptionText = styled(Typography)({
  fontSize: '11px',
  fontWeight: '400 !important',
  lineHeight: '18px',
  textAlign: 'left',
  wordWrap: 'break-word',
  overflowWrap: 'break-word',
  hyphens: 'auto',
  wordBreak: 'break-word',
});

const RetryBadge = styled('div')(({ theme }) => ({
  fontSize: '11px',
  lineHeight: '14px',
  backgroundColor: theme.palette.action.hover,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '999px',
  padding: '2px 6px',
  color: theme.palette.text.secondary,
  whiteSpace: 'nowrap',
}));

const ComponentDisplayName = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  color: theme.palette.text.primary,
}));

export function ComponentNodeBodyView(props) {
  const collapsed = useZoomCollapse();
  const hasDescription = !!props.data.description;
  const hasAiInstructions =
    props.data.instructions &&
    (props.data.instructions.buildInstruction || props.data.instructions.editInstruction);
  const showNodeBody = hasDescription || hasAiInstructions;

  // Check if component is in INPROGRESS or WAITING state (workflow execution)
  const isInProgress = props.data.sessionData && props.data.sessionData.status === 'INPROGRESS';
  const isWaiting = props.data.sessionData && props.data.sessionData.status === 'WAITING';

  // Check if component is being built by AI (AI building process)
  const isAIBuilding = props.data.isBuilding || false;

  const shouldAnimate = isInProgress || isWaiting || isAIBuilding;
  const retryCount = props.data?.sessionData?.retryCount;
  const showRetryCount = typeof retryCount === 'number' && retryCount > 0;

  // Determine animation type and colors
  let animationType = null;
  let animationColor = props.baseColor;

  if (isInProgress) {
    animationType = 'inprogress';
    animationColor = '#ff9d0b';
  } else if (isWaiting) {
    animationType = 'waiting';
    animationColor = '#5d6bc4';
  } else if (isAIBuilding) {
    animationType = 'aibuilding'; // Use new purple AI building animation
    animationColor = '#944BA8';
  }

  const hasAIDraftBanner = () => {
    const editStatus = props.data.editStatus || null;
    const isTemp = props.data.isTemp || false;
    const isBuilding = props.data.isBuilding || false;
    const inWorkflowProgressView = props.data.modalData && 'hasRun' in props.data.modalData;
    return !inWorkflowProgressView ? !!(editStatus || isTemp || isBuilding) : false;
  };

  return (
    <NodeBodyContainer
      container
      baseColor={props.baseColor}
      type={props.data.type}
      className={`${props.selectedClass && props.selectedClass} component-node-container`}
      direction="column"
      animationType={animationType}
      hasaidraftbanner={hasAIDraftBanner(props)}
      hasloop={props.hasLoop}
    >
      {shouldAnimate && !isAIBuilding && (
        <AnimationContainer>
          {isInProgress ? (
            <CircularProgress size={16} style={{ color: animationColor }} />
          ) : (
            <TypingDots color={animationColor}>
              <Dot delay="0s" />
              <Dot delay="0.2s" />
              <Dot delay="0.4s" />
            </TypingDots>
          )}
        </AnimationContainer>
      )}

      <HeaderContainer
        container
        item
        baseColor={props.baseColor}
        stateColor={isAIBuilding ? null : animationColor}
        collapsed={collapsed}
      >
        <HeaderTitleGrid container item xs={12} direction="row" spacing={1} alignItems="flex-start">
          <Grid item>
            <img
              src={props.data.logo}
              alt={COMPONENT_ICON_ALT}
              className={`${props.classes.svg}`}
              width={collapsed ? 36 : 24}
              height={collapsed ? 36 : 24}
              style={{ transition: 'width 0.2s ease, height 0.2s ease' }}
            />
          </Grid>
          <Grid item>
            <ComponentDisplayName
              sx={{
                fontSize: collapsed ? '22px' : undefined,
                transition: 'font-size 0.2s ease',
              }}
            >
              {props.data.displayName}
            </ComponentDisplayName>
          </Grid>
          {showRetryCount && (
            <Grid item sx={{ marginLeft: 'auto' }}>
              <RetryBadge sx={collapsed ? { fontSize: '14px', lineHeight: '18px' } : undefined}>
                Auto-retries {retryCount}
              </RetryBadge>
            </Grid>
          )}
        </HeaderTitleGrid>
        {props.data.label && (
          <Grid item xs={12}>
            <ComponentLabelText
              title={props.data.label}
              sx={{
                fontSize: collapsed ? '16px' : undefined,
                lineHeight: collapsed ? '22px' : undefined,
                transition: 'font-size 0.2s ease',
              }}
            >
              {props.data.label}
            </ComponentLabelText>
          </Grid>
        )}
      </HeaderContainer>
      <Collapse in={showNodeBody && !collapsed} timeout={200}>
        <ComponentDescriptionContainer item container>
          {isAIBuilding ? (
            <Grid item>
              <AILoadingAnimation
                messages={['Building...', 'Processing...', 'Generating...']}
                showMessage={false}
                tokenCount={60}
                containerSx={{
                  minHeight: '40px',
                  padding: '8px 0',
                  background: 'transparent',
                  border: 'none',
                  width: '100%',
                  '& > div:last-child': {
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '2px',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    width: '100%',
                    '& > div': {
                      minWidth: '12px',
                      maxWidth: '16px',
                      fontSize: '6px !important',
                      height: '10px',
                      flex: 'none',
                    },
                  },
                }}
              />
            </Grid>
          ) : (
            <>
              {hasDescription && (
                <Grid item>
                  <ComponentDescriptionText>{props.data.description}</ComponentDescriptionText>
                </Grid>
              )}
              {hasAiInstructions && <AiInstructionInfoText data={props.data} />}
            </>
          )}
        </ComponentDescriptionContainer>
      </Collapse>
    </NodeBodyContainer>
  );
}

ComponentNodeBodyView.propTypes = {
  baseColor: PropTypes.any,
  selectedClass: PropTypes.any,
  src: PropTypes.any,
  svg: PropTypes.any,
  displayName: PropTypes.any,
  label: PropTypes.any,
  showNodeBody: PropTypes.any,
  hasDescription: PropTypes.bool,
  description: PropTypes.any,
  hasAiInstructions: PropTypes.any,
  data: PropTypes.any,
  parent: PropTypes.any,
  hasLoop: PropTypes.bool,
};
